import type { Ruleset } from '@ic/engine';

// Saved games and custom rulesets live in IndexedDB so they work offline,
// and are pushed to the server by sync.ts when the user is signed in.

export interface SavedRuleset { id: string; ruleset: Ruleset; updatedAt: number; synced: boolean }
export interface SavedGame {
  id: string;
  mode: 'local' | 'bot' | 'online';
  rulesetName: string;
  ruleset: Ruleset;
  white: string;
  black: string;
  result: string;
  reason: string;
  moves: string[];
  playedAt: number;
  synced: boolean;
}

type StoreName = 'rulesets' | 'games';
let dbp: Promise<IDBDatabase> | null = null;

function db(): Promise<IDBDatabase> {
  dbp ??= new Promise((resolve, reject) => {
    const req = indexedDB.open('illegalchess', 1);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains('rulesets')) d.createObjectStore('rulesets', { keyPath: 'id' });
      if (!d.objectStoreNames.contains('games')) d.createObjectStore('games', { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbp;
}

async function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const r = fn(d.transaction(store, mode).objectStore(store));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export const idb = {
  all: <T,>(store: StoreName) => tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>).catch(() => [] as T[]),
  put: <T,>(store: StoreName, v: T) => tx(store, 'readwrite', (s) => s.put(v)).catch(() => undefined),
  del: (store: StoreName, id: string) => tx(store, 'readwrite', (s) => s.delete(id)).catch(() => undefined),
};
